// Shared info cards for Pylon tickets, doctors, and patients. Used by the
// ticket page ("Doctor & Patient Info") and the homepage agent chat, which
// renders them for entities the agent retrieves mid-conversation.

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatTimeTo12Hour } from "../Oncall/utils";
import { getPatientData } from "./patientApi";
import {
  supportContextCardDoctor,
  supportContextCardPatient,
  supportContextCardTitle,
  supportInsetPanelAuto,
  supportSubpanelHeader,
} from "./supportStyles";
import {
  supportDoctorPath,
  supportPatientPath,
  supportTicketPath,
} from "./supportUrls";
import { formatTicketTimestamp } from "./ticketUtils";

const STATE_BADGE_CLASSES = {
  closed: "bg-gray-100 text-gray-700 border-gray-300",
  on_hold: "bg-amber-50 text-amber-700 border-amber-300",
  waiting_on_customer: "bg-amber-50 text-amber-700 border-amber-300",
  new: "bg-blue-50 text-blue-700 border-blue-300",
};

export const TicketStateBadge = ({ state }) => (
  <span
    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${
      STATE_BADGE_CLASSES[state] || "bg-green-50 text-green-700 border-green-300"
    }`}
  >
    {state?.replace(/_/g, " ") || "unknown"}
  </span>
);

export const TicketRow = ({ label, value }) => (
  <div className="grid grid-cols-[7.25rem_minmax(0,1fr)] gap-x-3 items-start py-0.5">
    <dt className="text-[11px] text-gray-500 leading-snug pt-0.5">{label}</dt>
    <dd className="text-sm font-medium text-gray-900 break-words min-w-0 leading-snug">
      {value === null || value === undefined || value === "" ? "—" : value}
    </dd>
  </div>
);

export const TicketSummaryCard = ({ ticket, requester, linkToTicketPage }) => (
  <div className={`${supportInsetPanelAuto} overflow-hidden`}>
    <div className={`${supportSubpanelHeader} flex items-center justify-between gap-2`}>
      <h4 className={supportContextCardTitle}>
        Pylon Ticket #{ticket.number ?? ticket.id}
      </h4>
    <TicketStateBadge state={ticket.state} />
    </div>
    <dl className="px-3 py-2.5 space-y-0">
      <TicketRow label="Title" value={ticket.title} />
      <TicketRow label="Requester" value={requester?.name || ticket.requester_email} />
      <TicketRow label="Requester email" value={requester?.email || ticket.requester_email} />
      <TicketRow label="Created" value={formatTicketTimestamp(ticket.created_at)} />
      <TicketRow label="Updated" value={formatTicketTimestamp(ticket.updated_at)} />
      <TicketRow
        label="Tags"
        value={ticket.tags?.length ? ticket.tags.join(", ") : null}
      />
      {ticket.link && (
        <TicketRow
          label="Pylon link"
          value={
            <a
              href={ticket.link}
              target="_blank"
              rel="noreferrer"
              className="text-ktx-purple hover:underline"
            >
              Open in Pylon
            </a>
          }
        />
      )}
      {linkToTicketPage && (
        <div className="pt-2">
          <Link
            to={supportTicketPath(ticket.number ?? ticket.id)}
            className="text-sm font-medium text-ktx-purple hover:underline"
          >
            Open ticket page
          </Link>
        </div>
      )}
    </dl>
  </div>
);

export const DoctorCard = ({ doctor, requester }) => (
  <div className={`${supportInsetPanelAuto} overflow-hidden ${supportContextCardDoctor}`}>
    <div className={supportSubpanelHeader}>
      <h4 className={supportContextCardTitle}>Doctor</h4>
    </div>
    {doctor ? (
      <dl className="px-3 py-2.5 space-y-0">
        <TicketRow label="Name" value={doctor.user_name} />
        <TicketRow label="Organization" value={doctor.organization} />
        <TicketRow label="Super org" value={doctor.super_org} />
        <TicketRow label="Specialty" value={doctor.specialty} />
        <TicketRow label="Model type" value={doctor.model_type} />
        <TicketRow label="Email" value={doctor.user_email} />
        <div className="pt-2">
          <Link
            to={supportDoctorPath(doctor.user_id)}
            className="text-sm font-medium text-ktx-purple hover:underline"
            target="_blank"
            rel="noreferrer"
          >
            Open doctor page
          </Link>
        </div>
      </dl>
    ) : (
      <p className="px-3 py-2.5 text-sm text-gray-500">
        No doctor found matching the requester email
        {requester?.email ? ` (${requester.email})` : ""}.
      </p>
    )}
  </div>
);

export const PatientCard = ({ patient, isLoading, patientId }) => {
  const appointmentDate = patient?.date_of_appt?.trim() || null;
  const appointmentTime = patient?.appt_time?.trim()
    ? formatTimeTo12Hour(patient.appt_time)
    : null;
  const appointment =
    appointmentDate && appointmentTime
      ? `${appointmentDate} ${appointmentTime}`
      : appointmentDate || appointmentTime;

  return (
    <div
      className={`${supportInsetPanelAuto} overflow-hidden ${supportContextCardPatient}`}
    >
      <div className={supportSubpanelHeader}>
        <h4 className={supportContextCardTitle}>Patient</h4>
      </div>
      {isLoading ? (
        <p className="px-3 py-2.5 text-sm text-gray-500">
          Loading patient {patientId}…
        </p>
      ) : patient ? (
        <dl className="px-3 py-2.5 space-y-0">
          <TicketRow label="Name" value={patient.description} />
          <TicketRow label="Patient ID" value={patient.patient_id} />
          <TicketRow label="MRN" value={patient.mrn} />
          <TicketRow label="Patient type" value={patient.patient_type} />
          <TicketRow label="Status" value={patient.status} />
          <TicketRow label="Appointment" value={appointment} />
          <TicketRow label="Import type" value={patient.import_type} />
          <TicketRow label="EHR upload" value={patient.ehr_upload_status} />
          <div className="pt-2">
            <Link
              to={supportPatientPath(patient.patient_id)}
              className="text-sm font-medium text-ktx-purple hover:underline"
            >
              Open patient page
            </Link>
          </div>
        </dl>
      ) : (
        <p className="px-3 py-2.5 text-sm text-gray-500">
          Patient {patientId} not found.
        </p>
      )}
    </div>
  );
};

/** PatientCard that loads its own record from just a patient ID. */
export const PatientCardById = ({ patientId }) => {
  const [state, setState] = useState({ loading: true, patient: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, patient: null });
    getPatientData(patientId).then((data) => {
      if (cancelled) return;
      setState({
        loading: false,
        patient:
          data && !data.error && Object.keys(data).length > 0 ? data : null,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  return (
    <PatientCard
      patientId={patientId}
      patient={state.patient}
      isLoading={state.loading}
    />
  );
};
